import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Flex, H2, Button, Input, Close, H4, Line, InputModal, ToolTip } from '../../lib/components'
import { InfoIcon } from '../../lib/components/Icon'
import {
  adminTeamServices,
  AdminRosterRow,
  AdminRoleDefinitionRow,
} from '../../services/admin-team-service'
import { RootState } from '../../store'
import { showBanner } from '../../global-state/banner-slice'
import {
  ADMIN_FEATURE_KEYS,
  AdminFeatureKey,
  FeatureAccessLevel,
  getDefaultPermissionsForRole,
} from '../../lib/permissions/adminFeatures'
import {
  getDefaultActionGrantsForRole,
  normalizeActionGrantsFromLogin,
} from '../../lib/permissions/adminActions'
import {
  ADMIN_ROUTE_MATRIX,
  ADMIN_ROUTE_MATRIX_ACTION_KEYS,
  ADMIN_ROUTE_MATRIX_FEATURE_KEYS,
} from '../../lib/permissions/adminRouteMatrix'
import { UserRole } from '../../lib/types/userTypes'
import './admin-team.scss'

const emptyPermissions = (): Record<AdminFeatureKey, FeatureAccessLevel> => {
  const o = {} as Record<AdminFeatureKey, FeatureAccessLevel>
  ADMIN_FEATURE_KEYS.forEach((k) => {
    o[k] = 'none'
  })
  return o
}

const normalizePermissions = (
  raw: Record<string, string> | undefined,
): Record<AdminFeatureKey, FeatureAccessLevel> => {
  const base = emptyPermissions()
  if (!raw) return base
  ADMIN_FEATURE_KEYS.forEach((k) => {
    const v = raw[k]
    if (v === 'none' || v === 'read' || v === 'write') {
      base[k] = v
    }
  })
  return base
}

const MATRIX_PATH_TOOLTIP_CLASSES = ['tool-tip-container', 'admin-team-page__route-matrix-tooltip']

function RouteMatrixPathTooltipBody({ path }: { path: string }) {
  if (path === '—') {
    return (
      <div className='admin-team-page__path-tooltip'>
        <p className='admin-team-page__path-tooltip-lead'>Button action</p>
        <p className='admin-team-page__path-tooltip-muted'>
          This row controls a button in the admin app. It is not tied to a single page URL.
        </p>
      </div>
    )
  }
  const qIndex = path.indexOf('?')
  const rawPath = qIndex >= 0 ? path.slice(0, qIndex) : path
  const query = qIndex >= 0 ? path.slice(qIndex + 1) : ''
  const withLeading = rawPath.startsWith('/') ? rawPath : `/${rawPath}`
  const segments = withLeading.split('/').filter(Boolean)
  const fullDisplay = query ? `${withLeading}?${query}` : withLeading
  return (
    <div className='admin-team-page__path-tooltip'>
      <p className='admin-team-page__path-tooltip-lead'>Route path</p>
      <div className='admin-team-page__path-tooltip-full'>
        <code>{fullDisplay}</code>
      </div>
      {segments.length > 0 ? (
        <ol className='admin-team-page__path-tooltip-segments' aria-label='Path segments'>
          {segments.map((seg, i) => (
            <li key={`${i}-${seg}`}>
              <span className='admin-team-page__path-tooltip-seg'>{seg}</span>
            </li>
          ))}
        </ol>
      ) : null}
      {query ? (
        <p className='admin-team-page__path-tooltip-query'>
          <span className='admin-team-page__path-tooltip-query-label'>Query</span>
          <code>{query}</code>
        </p>
      ) : null}
    </div>
  )
}

const AdminTeamContainer = () => {
  const dispatch = useDispatch()
  const { userId: currentUserId, role: currentUserRole } = useSelector((s: RootState) => s.gbcUser)
  const isSuperuserActor = currentUserRole === UserRole.SUPERUSER
  const [admins, setAdmins] = useState<AdminRosterRow[]>([])
  const [roleDefs, setRoleDefs] = useState<AdminRoleDefinitionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [rowRoles, setRowRoles] = useState<Record<number, string>>({})
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'CREDIBILA_ADMIN',
  })

  const [passwordResetModal, setPasswordResetModal] = useState<{
    temporaryPassword: string
    email: string
  } | null>(null)

  const [showRoleEditor, setShowRoleEditor] = useState(false)
  const [roleEditorMode, setRoleEditorMode] = useState<'create' | 'edit'>('create')
  const [editingCode, setEditingCode] = useState<string | null>(null)
  const [roleForm, setRoleForm] = useState({
    code: '',
    display_name: '',
    permissions: emptyPermissions(),
    actionGrants: getDefaultActionGrantsForRole('CREDIBILA_ADMIN'),
  })

  const assignableRoleCodes = useMemo(
    () => roleDefs.filter((r) => r.code !== 'SUPERUSER').map((r) => r.code),
    [roleDefs],
  )

  const load = useCallback(async () => {
    setLoading(true)
    const [adminsRes, rolesRes]: any[] = await Promise.all([
      adminTeamServices.listAdmins(),
      adminTeamServices.listRoleDefinitions(),
    ])
    if (adminsRes?.status === 200 && Array.isArray(adminsRes.data)) {
      setAdmins(adminsRes.data)
      const m: Record<number, string> = {}
      adminsRes.data.forEach((a: AdminRosterRow) => {
        m[a.id] = a.role
      })
      setRowRoles(m)
    } else {
      dispatch(
        showBanner({
          text: adminsRes?.data?.error || adminsRes?.data?.message || 'Failed to load admins',
          variant: 'error-banner',
        }),
      )
    }
    if (rolesRes?.status === 200 && Array.isArray(rolesRes.data)) {
      setRoleDefs(rolesRes.data)
    } else {
      dispatch(
        showBanner({
          text: rolesRes?.data?.error || rolesRes?.data?.message || 'Failed to load roles',
          variant: 'error-banner',
        }),
      )
    }
    setLoading(false)
  }, [dispatch])

  useEffect(() => {
    load()
  }, [load])

  const applyPermissionTemplate = (template: 'CREDIBILA_ADMIN' | 'CREDIBILA_DEV' | 'none') => {
    if (template === 'none') {
      setRoleForm((f) => ({
        ...f,
        permissions: emptyPermissions(),
        actionGrants: getDefaultActionGrantsForRole('CREDIBILA_DEV'),
      }))
      return
    }
    const next = { ...getDefaultPermissionsForRole(template) }
    setRoleForm((f) => ({
      ...f,
      permissions: next,
      actionGrants: getDefaultActionGrantsForRole(template),
    }))
  }

  const applyMatrixReadAll = () => {
    setRoleForm((f) => {
      const permissions = { ...f.permissions }
      for (const feat of ADMIN_ROUTE_MATRIX_FEATURE_KEYS) {
        permissions[feat] = 'read'
      }
      return { ...f, permissions }
    })
  }

  const applyMatrixWriteAll = () => {
    setRoleForm((f) => {
      const permissions = { ...f.permissions }
      for (const feat of ADMIN_ROUTE_MATRIX_FEATURE_KEYS) {
        permissions[feat] = 'write'
      }
      return { ...f, permissions }
    })
  }

  const applyMatrixNoneAll = () => {
    setRoleForm((f) => {
      const permissions = { ...f.permissions }
      for (const feat of ADMIN_ROUTE_MATRIX_FEATURE_KEYS) {
        permissions[feat] = 'none'
      }
      return { ...f, permissions }
    })
  }

  const applyMatrixAllowAll = () => {
    setRoleForm((f) => {
      const actionGrants = { ...f.actionGrants }
      for (const act of ADMIN_ROUTE_MATRIX_ACTION_KEYS) {
        actionGrants[act] = true
      }
      return { ...f, actionGrants }
    })
  }

  const applyMatrixDenyAll = () => {
    setRoleForm((f) => {
      const actionGrants = { ...f.actionGrants }
      for (const act of ADMIN_ROUTE_MATRIX_ACTION_KEYS) {
        actionGrants[act] = false
      }
      return { ...f, actionGrants }
    })
  }

  const openCreateRole = () => {
    setRoleEditorMode('create')
    setEditingCode(null)
    setRoleForm({
      code: '',
      display_name: '',
      permissions: emptyPermissions(),
      actionGrants: getDefaultActionGrantsForRole('CREDIBILA_ADMIN'),
    })
    setShowRoleEditor(true)
  }

  const openEditRole = (row: AdminRoleDefinitionRow) => {
    setRoleEditorMode('edit')
    setEditingCode(row.code)
    const raw = row.permissions as Record<string, unknown>
    const actionsRaw =
      raw && typeof raw.actions === 'object' && raw.actions !== null
        ? (raw.actions as Record<string, unknown>)
        : undefined
    setRoleForm({
      code: row.code,
      display_name: row.display_name,
      permissions: normalizePermissions(row.permissions),
      actionGrants: normalizeActionGrantsFromLogin(row.code, actionsRaw),
    })
    setShowRoleEditor(true)
  }

  const handleSaveRoleDefinition = async () => {
    if (roleEditorMode === 'create') {
      const code = roleForm.code.trim().toUpperCase()
      if (!/^[A-Z][A-Z0-9_]*$/.test(code)) {
        dispatch(
          showBanner({
            text: 'Role code must start with a letter and use only A–Z, digits, or underscores (e.g. CREDIBILA_ANALYST).',
            variant: 'error-banner',
          }),
        )
        return
      }
      const res: any = await adminTeamServices.createRoleDefinition({
        code,
        display_name: roleForm.display_name.trim(),
        permissions: {
          ...roleForm.permissions,
          actions: roleForm.actionGrants,
        } as unknown as Record<string, string>,
      })
      if (res?.status === 201) {
        dispatch(showBanner({ text: 'Role created', variant: 'success-banner' }))
        setShowRoleEditor(false)
        await load()
      } else {
        dispatch(
          showBanner({
            text: res?.data?.error || res?.data?.message || 'Create role failed',
            variant: 'error-banner',
          }),
        )
      }
    } else if (editingCode) {
      const res: any = await adminTeamServices.updateRoleDefinition(editingCode, {
        display_name: roleForm.display_name.trim(),
        permissions: {
          ...roleForm.permissions,
          actions: roleForm.actionGrants,
        } as unknown as Record<string, string>,
      })
      if (res?.status === 200) {
        dispatch(showBanner({ text: 'Role updated', variant: 'success-banner' }))
        setShowRoleEditor(false)
        await load()
      } else {
        dispatch(
          showBanner({
            text: res?.data?.error || res?.data?.message || 'Update role failed',
            variant: 'error-banner',
          }),
        )
      }
    }
  }

  const handleDeleteRole = async (code: string) => {
    if (!window.confirm(`Remove role "${code}"? No admin may still have this role.`)) return
    const res: any = await adminTeamServices.deleteRoleDefinition(code)
    if (res?.status === 204 || res?.status === 200) {
      dispatch(showBanner({ text: 'Role removed', variant: 'success-banner' }))
      await load()
    } else {
      dispatch(
        showBanner({
          text: res?.data?.error || res?.data?.message || 'Remove role failed',
          variant: 'error-banner',
        }),
      )
    }
  }

  const handleUpdateRole = async (adminId: number) => {
    const role = rowRoles[adminId]
    const res: any = await adminTeamServices.updateAdminRole(adminId, role)
    if (res?.status === 200) {
      dispatch(showBanner({ text: 'Role updated', variant: 'success-banner' }))
      await load()
    } else {
      dispatch(
        showBanner({
          text: res?.data?.error || res?.data?.message || 'Update failed',
          variant: 'error-banner',
        }),
      )
    }
  }

  const handleDelete = async (adminId: number) => {
    if (!window.confirm('Remove this admin account? They will be signed out.')) return
    const res: any = await adminTeamServices.deleteAdmin(adminId)
    if (res?.status === 204 || res?.status === 200) {
      dispatch(showBanner({ text: 'Admin removed', variant: 'success-banner' }))
      await load()
    } else {
      dispatch(
        showBanner({
          text: res?.data?.error || res?.data?.message || 'Delete failed',
          variant: 'error-banner',
        }),
      )
    }
  }

  const handleCreate = async () => {
    const res: any = await adminTeamServices.createAdmin(createForm)
    if (res?.status === 201) {
      dispatch(showBanner({ text: 'Admin created', variant: 'success-banner' }))
      setShowAdd(false)
      setCreateForm({
        name: '',
        email: '',
        password: '',
        role: assignableRoleCodes[0] || 'CREDIBILA_ADMIN',
      })
      await load()
    } else {
      dispatch(
        showBanner({
          text: res?.data?.error || res?.data?.message || 'Create failed',
          variant: 'error-banner',
        }),
      )
    }
  }

  const handleResetPassword = async (adminId: number, email: string) => {
    if (
      !window.confirm(
        `Generate a new temporary password for ${email}? Their active sessions will end immediately.`,
      )
    ) {
      return
    }
    const res: any = await adminTeamServices.resetAdminPassword(adminId)
    if (res?.status === 200 && res.data?.temporaryPassword) {
      setPasswordResetModal({
        temporaryPassword: res.data.temporaryPassword,
        email: res.data.email ?? email,
      })
      dispatch(
        showBanner({
          text: 'New temporary password generated. Copy it from the dialog and share it securely.',
          variant: 'success-banner',
        }),
      )
      await load()
    } else {
      dispatch(
        showBanner({
          text: res?.data?.message || res?.data?.error || 'Password reset failed',
          variant: 'error-banner',
        }),
      )
    }
  }

  const copyTemporaryPassword = async () => {
    if (!passwordResetModal) return
    try {
      await navigator.clipboard.writeText(passwordResetModal.temporaryPassword)
      dispatch(showBanner({ text: 'Password copied to clipboard', variant: 'success-banner' }))
    } catch {
      dispatch(
        showBanner({
          text: 'Copy failed — select the password field and copy manually.',
          variant: 'error-banner',
        }),
      )
    }
  }

  return (
    <Flex variant='column-center' classes={['admin-team-page']}>
      <H2 classes={['mb-8']} text='Admin team' />
      <p className='admin-team-page__hint mb-24'>
        Define <strong>roles</strong> (codes such as CREDIBILA_ADMIN or CREDIBILA_DEV) and their
        feature access, then invite <strong>admin users</strong> and assign them a role.
      </p>

      <H2 classes={['admin-team-page__section-title', 'mb-8']} text='Roles' />
      <p className='admin-team-page__hint mb-12'>
        Superuser can add or remove custom roles. Built-in roles cannot be deleted. Use{' '}
        <strong>New custom role</strong> to create another role code in the same style as
        CREDIBILA_ADMIN / CREDIBILA_DEV, then set <strong>feature permissions</strong> below in the
        form (or start from a template).
      </p>
      <div className='admin-team-page__toolbar mb-16'>
        <Button
          variant='primary-btn'
          text='New custom role'
          classes={['auto-btn-width']}
          handleClick={openCreateRole}
        />
      </div>

      <div className='admin-team-page__table-wrap table_scroll_horizontal mb-32'>
        <table className='styled-table'>
          <thead>
            <tr>
              <th>Role code</th>
              <th>Display name</th>
              <th>Built-in</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {roleDefs.map((r) => (
              <tr key={r.code}>
                <td>
                  <code>{r.code}</code>
                </td>
                <td>{r.display_name}</td>
                <td>{r.is_system ? 'Yes' : 'No'}</td>
                <td>
                  <Flex variant='row'>
                    <Button
                      variant='secondary-btn'
                      text='Edit role & permissions'
                      classes={['auto-btn-width', 'mr-8']}
                      handleClick={() => openEditRole(r)}
                    />
                    {!r.is_system ? (
                      <Button
                        variant='secondary-btn'
                        text='Remove role'
                        classes={['auto-btn-width']}
                        handleClick={() => handleDeleteRole(r.code)}
                      />
                    ) : null}
                  </Flex>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <H2 classes={['admin-team-page__section-title', 'mb-8']} text='Admin accounts' />
      {isSuperuserActor ? (
        <p className='admin-team-page__hint mb-12'>
          Superuser can use <strong>Reset password</strong> for any admin (including other
          superusers). A one-time temporary password is shown in a dialog; all sessions for that
          account end immediately.
        </p>
      ) : null}
      <div className='admin-team-page__toolbar mb-16'>
        <Button
          variant='primary-btn'
          text='Add admin user'
          classes={['auto-btn-width']}
          handleClick={() => setShowAdd(true)}
        />
      </div>

      {passwordResetModal ? (
        <InputModal onBackdropClick={() => setPasswordResetModal(null)}>
          <Flex variant='column' classes={['input-modal', 'p-24']}>
            <Flex variant='space-between' classes={['mb-8']}>
              <H4 text='Temporary password' />
              <Close height='24px' onClick={() => setPasswordResetModal(null)} />
            </Flex>
            <Line />
            <p className='admin-team-page__hint mb-12' style={{ textAlign: 'left' }}>
              Account: <strong>{passwordResetModal.email}</strong>
            </p>
            <p className='admin-team-page__hint mb-12' style={{ textAlign: 'left' }}>
              This password is shown only here. Store or share it through a secure channel before
              you close this dialog.
            </p>
            <Input
              name='temporaryPassword'
              labelText='New password'
              type='text'
              value={passwordResetModal.temporaryPassword}
              handleChange={() => {}}
              disabled
            />
            <div className='admin-team-page__modal-footer'>
              <Button
                variant='secondary-btn'
                text='Close'
                classes={['auto-btn-width']}
                handleClick={() => setPasswordResetModal(null)}
              />
              <Button
                variant='primary-btn'
                text='Copy password'
                classes={['auto-btn-width']}
                handleClick={copyTemporaryPassword}
              />
            </div>
          </Flex>
        </InputModal>
      ) : null}

      {showAdd ? (
        <InputModal onBackdropClick={() => setShowAdd(false)}>
          <Flex variant='column' classes={['input-modal', 'input-modal--scrollable', 'p-24']}>
            <Flex variant='space-between' classes={['mb-8']}>
              <H4 text='Add admin user' />
              <Close height='24px' onClick={() => setShowAdd(false)} />
            </Flex>
            <Line />
            <Input
              name='name'
              labelText='Name'
              value={createForm.name}
              handleChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
            />
            <Input
              name='email'
              labelText='Email'
              value={createForm.email}
              handleChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
            />
            <Input
              name='password'
              labelText='Password'
              type='password'
              value={createForm.password}
              handleChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
            />
            <label className='mb-8 mt-8'>Assign role</label>
            <select
              className='admin-team-page__perm-select mb-16'
              value={createForm.role}
              onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
            >
              {assignableRoleCodes.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
            <div className='admin-team-page__modal-footer'>
              <Button
                variant='secondary-btn'
                text='Cancel'
                classes={['auto-btn-width']}
                handleClick={() => setShowAdd(false)}
              />
              <Button
                variant='primary-btn'
                text='Create admin'
                classes={['auto-btn-width']}
                handleClick={handleCreate}
              />
            </div>
          </Flex>
        </InputModal>
      ) : null}

      {showRoleEditor ? (
        <InputModal onBackdropClick={() => setShowRoleEditor(false)}>
          <Flex
            variant='column'
            classes={['input-modal', 'input-modal--scrollable', 'input-modal--role-matrix', 'p-24']}
          >
            <Flex variant='space-between' classes={['mb-8']}>
              <H4
                text={
                  roleEditorMode === 'create'
                    ? 'New custom role'
                    : `Edit role: ${editingCode ?? ''}`
                }
              />
              <Close height='24px' onClick={() => setShowRoleEditor(false)} />
            </Flex>
            <Line />
            <p className='admin-team-page__hint mb-16'>
              {roleEditorMode === 'create'
                ? 'Pick a unique role code in the same style as CREDIBILA_ADMIN or CREDIBILA_DEV (uppercase letters, numbers, underscores). Then set the route matrix, or start from a template.'
                : 'Update the display name and route matrix for this role.'}
            </p>
            {roleEditorMode === 'create' ? (
              <Input
                name='code'
                labelText='Role code (e.g. CREDIBILA_ANALYST)'
                value={roleForm.code}
                handleChange={(e) => setRoleForm({ ...roleForm, code: e.target.value })}
              />
            ) : (
              <p className='mb-12'>
                <strong>Role code:</strong> <code>{roleForm.code}</code>
              </p>
            )}
            <Input
              name='display_name'
              labelText='Display name'
              value={roleForm.display_name}
              handleChange={(e) => setRoleForm({ ...roleForm, display_name: e.target.value })}
            />
            {roleEditorMode === 'create' ? (
              <>
                <p className='admin-team-page__preset-label mt-16 mb-8'>
                  Route matrix — start from:
                </p>
                <div className='admin-team-page__preset-row'>
                  <Button
                    variant='basic-btn'
                    text='Same as CREDIBILA_ADMIN'
                    classes={['auto-btn-width']}
                    handleClick={() => applyPermissionTemplate('CREDIBILA_ADMIN')}
                  />
                  <Button
                    variant='basic-btn'
                    text='Same as CREDIBILA_DEV'
                    classes={['auto-btn-width']}
                    handleClick={() => applyPermissionTemplate('CREDIBILA_DEV')}
                  />
                  <Button
                    variant='basic-btn'
                    text='All none'
                    classes={['auto-btn-width']}
                    handleClick={() => applyPermissionTemplate('none')}
                  />
                </div>
              </>
            ) : null}
            <Flex
              variant='space-between'
              classes={['admin-team-page__matrix-header', 'mt-8', 'mb-8']}
            >
              <H4 classes={['mb-0']} text='Route matrix' />
              <div className='admin-team-page__matrix-bulk'>
                <Button
                  variant='basic-btn'
                  text='Read all'
                  classes={['admin-team-page__matrix-bulk-btn']}
                  handleClick={applyMatrixReadAll}
                />
                <Button
                  variant='basic-btn'
                  text='Write all'
                  classes={['admin-team-page__matrix-bulk-btn']}
                  handleClick={applyMatrixWriteAll}
                />
                <Button
                  variant='basic-btn'
                  text='None all'
                  classes={['admin-team-page__matrix-bulk-btn']}
                  handleClick={applyMatrixNoneAll}
                />
                <Button
                  variant='basic-btn'
                  text='Allow all'
                  classes={['admin-team-page__matrix-bulk-btn']}
                  handleClick={applyMatrixAllowAll}
                />
                <Button
                  variant='basic-btn'
                  text='Deny all'
                  classes={['admin-team-page__matrix-bulk-btn']}
                  handleClick={applyMatrixDenyAll}
                />
              </div>
            </Flex>
            <table className='admin-team-page__perm-table admin-team-page__perm-table--matrix'>
              <thead>
                <tr>
                  <th>Title</th>
                  <th className='admin-team-page__perm-table-access'>Access</th>
                </tr>
              </thead>
              <tbody>
                {ADMIN_ROUTE_MATRIX.map((row, index) =>
                  row.kind === 'route' ? (
                    <tr key={`route-${index}`}>
                      <td>
                        <span className='admin-team-page__matrix-title-cell'>
                          <span className='admin-team-page__matrix-title-text'>{row.title}</span>
                          <ToolTip
                            content={<RouteMatrixPathTooltipBody path={row.path} />}
                            contentClassNames={MATRIX_PATH_TOOLTIP_CLASSES}
                          >
                            <button
                              type='button'
                              className='admin-team-page__matrix-info-btn'
                              aria-label={`Route path for ${row.title}`}
                            >
                              <InfoIcon
                                width='18'
                                height='18'
                                classes={['admin-team-page__matrix-info-icon']}
                              />
                            </button>
                          </ToolTip>
                        </span>
                      </td>
                      <td className='admin-team-page__perm-table-access'>
                        <select
                          className='admin-team-page__perm-select admin-team-page__perm-select--matrix'
                          value={roleForm.permissions[row.feature]}
                          onChange={(e) =>
                            setRoleForm({
                              ...roleForm,
                              permissions: {
                                ...roleForm.permissions,
                                [row.feature]: e.target.value as FeatureAccessLevel,
                              },
                            })
                          }
                        >
                          <option value='none'>none</option>
                          <option value='read'>read</option>
                          <option value='write'>write</option>
                        </select>
                      </td>
                    </tr>
                  ) : (
                    <tr key={`action-${row.action}-${index}`}>
                      <td>
                        <span className='admin-team-page__matrix-title-cell'>
                          <span className='admin-team-page__matrix-title-text'>{row.title}</span>
                          <ToolTip
                            content={<RouteMatrixPathTooltipBody path={row.path} />}
                            contentClassNames={MATRIX_PATH_TOOLTIP_CLASSES}
                          >
                            <button
                              type='button'
                              className='admin-team-page__matrix-info-btn'
                              aria-label={`Details for ${row.title}`}
                            >
                              <InfoIcon
                                width='18'
                                height='18'
                                classes={['admin-team-page__matrix-info-icon']}
                              />
                            </button>
                          </ToolTip>
                        </span>
                      </td>
                      <td className='admin-team-page__perm-table-access'>
                        <select
                          className='admin-team-page__perm-select admin-team-page__perm-select--matrix'
                          value={roleForm.actionGrants[row.action] ? 'allow' : 'deny'}
                          onChange={(e) =>
                            setRoleForm({
                              ...roleForm,
                              actionGrants: {
                                ...roleForm.actionGrants,
                                [row.action]: e.target.value === 'allow',
                              },
                            })
                          }
                        >
                          <option value='allow'>allow</option>
                          <option value='deny'>deny</option>
                        </select>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
            <div className='admin-team-page__modal-footer'>
              <Button
                variant='secondary-btn'
                text='Cancel'
                classes={['auto-btn-width']}
                handleClick={() => setShowRoleEditor(false)}
              />
              <Button
                variant='primary-btn'
                text={roleEditorMode === 'create' ? 'Create role' : 'Save changes'}
                classes={['auto-btn-width']}
                handleClick={handleSaveRoleDefinition}
              />
            </div>
          </Flex>
        </InputModal>
      ) : null}

      {loading ? (
        <p>Loading…</p>
      ) : (
        <div className='admin-team-page__table-wrap table_scroll_horizontal'>
          <table className='styled-table'>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => {
                const isSuper = a.role === 'SUPERUSER'
                const isSelf = a.id === currentUserId
                return (
                  <tr key={a.id}>
                    <td>{a.name}</td>
                    <td>{a.email}</td>
                    <td>
                      {isSuper ? (
                        <code>{a.role}</code>
                      ) : (
                        <select
                          className='admin-team-page__perm-select'
                          value={rowRoles[a.id] ?? a.role}
                          disabled={isSelf}
                          onChange={(e) => setRowRoles({ ...rowRoles, [a.id]: e.target.value })}
                        >
                          {assignableRoleCodes.map((code) => (
                            <option key={code} value={code}>
                              {code}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td>
                      <Flex variant='row' classes={['admin-team-page__actions-cell']}>
                        {!isSuper && !isSelf ? (
                          <>
                            <Button
                              variant='secondary-btn'
                              text='Update role'
                              classes={['auto-btn-width', 'mr-8']}
                              handleClick={() => handleUpdateRole(a.id)}
                              isDisabled={(rowRoles[a.id] ?? a.role) === a.role}
                            />
                            <Button
                              variant='secondary-btn'
                              text='Remove'
                              classes={['auto-btn-width', 'mr-8']}
                              handleClick={() => handleDelete(a.id)}
                            />
                          </>
                        ) : null}
                        {isSuperuserActor ? (
                          <Button
                            variant='secondary-btn'
                            text='Reset password'
                            classes={['auto-btn-width']}
                            handleClick={() => handleResetPassword(a.id, a.email)}
                          />
                        ) : null}
                      </Flex>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </Flex>
  )
}

export { AdminTeamContainer }
